import type { Schema, Effect, Option } from 'effect';

import { makeCreateTokenForCustomer } from '../../request/backendApi/customer/makeCreateTokenForCustomer';
import {
  makeGetCustomerMe,
  type CustomerAuthPayload,
} from '../../request/backendApi/customer/makeGetCustomerMe';
import { makeRefreshCustomerToken } from '../../request/backendApi/customer/makeRefreshCustomerToken';
import { makeSubscribeNewsletter } from '../../request/backendApi/customer/makeSubscribeNewsletter';
import {
  makeUpdateCustomer,
  type CustomerUpdateRequestBody,
} from '../../request/backendApi/customer/makeUpdateCustomer';

export type CustomerModuleDeps<
  CustomerTokenReadSchema extends Schema.Top,
  CustomerMeSchema extends Schema.Top,
  NewsletterSubscribeInputSchema extends Schema.Top,
  CustomerShowSchema extends Schema.Top,
  CustomerUpdateInput,
  AuthRequirements,
> = {
  readonly customerTokenRead: CustomerTokenReadSchema;
  readonly customerMe: CustomerMeSchema;
  readonly newsletterSubscribeInput: NewsletterSubscribeInputSchema;
  readonly customerShow: CustomerShowSchema;
  readonly customerEmail: (me: CustomerMeSchema['Type']) => string;
  readonly customerShowEmail: (customer: CustomerShowSchema['Type']) => string;
  readonly customerUpdateBody: (data: CustomerUpdateInput) => CustomerUpdateRequestBody;
  readonly decodeAuthTokenFromCookies: Effect.Effect<
    Option.Option<CustomerAuthPayload>,
    never,
    AuthRequirements
  >;
};

export const makeCustomerModule = <
  CustomerTokenReadSchema extends Schema.Top,
  CustomerMeSchema extends Schema.Top,
  NewsletterSubscribeInputSchema extends Schema.Top,
  CustomerShowSchema extends Schema.Top,
  CustomerUpdateInput,
  AuthRequirements,
>(
  deps: CustomerModuleDeps<
    CustomerTokenReadSchema,
    CustomerMeSchema,
    NewsletterSubscribeInputSchema,
    CustomerShowSchema,
    CustomerUpdateInput,
    AuthRequirements
  >
) => {
  const { CreateCustomerTokenError, createCustomerToken } = makeCreateTokenForCustomer({
    customerTokenRead: deps.customerTokenRead,
  });
  const { GetCustomerMeError, requestCustomerMe } = makeGetCustomerMe({
    customerMe: deps.customerMe,
    customerEmail: deps.customerEmail,
    decodeAuthTokenFromCookies: deps.decodeAuthTokenFromCookies,
  });
  const { RefreshCustomerTokenError, refreshCustomerToken } = makeRefreshCustomerToken({
    customerTokenRead: deps.customerTokenRead,
  });
  const { SubscribeNewsletterError, subscribeNewsletter } = makeSubscribeNewsletter({
    newsletterSubscribeInput: deps.newsletterSubscribeInput,
  });
  const { UpdateCustomerError, updateCustomer } = makeUpdateCustomer({
    customerShow: deps.customerShow,
    customerShowEmail: deps.customerShowEmail,
    customerUpdateBody: deps.customerUpdateBody,
  });

  return {
    CreateCustomerTokenError,
    GetCustomerMeError,
    RefreshCustomerTokenError,
    SubscribeNewsletterError,
    UpdateCustomerError,
    createCustomerToken,
    requestCustomerMe,
    refreshCustomerToken,
    subscribeNewsletter,
    updateCustomer,
  };
};
